import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Fade,
  Zoom
} from '@mui/material';
import { kpiData, chartConfigs } from './data/kpiData';
import { KPIIndicator, ModalData } from './types';
import KPICategory from './components/KPICategory';
import ChartModal from './components/ChartModal';
import { performAIAnalysis, KPIData as AIKPIData } from './services/aiService';
import AIChatDialog from './components/AIChatDialog';
import {
  HEADER_CONSTANTS,
  NOTIFICATION_MESSAGES,
  BACKGROUND_GRADIENTS,
  PARTICLE_CONFIG,
  PARTICLE_KEYFRAMES,
  STYLE_CONSTANTS,
  ERROR_MESSAGES,
  LOG_MESSAGES
} from './constants';
import {
  CATEGORY_CONFIGS,
  GRID_CONFIGS,
  THEME_CONFIG,
  ANIMATION_CONFIG,
  NOTIFICATION_CONFIG,
  MODAL_CONFIG,
  LOADING_CONFIG,
  BREAKPOINT_CONFIG,
  SPACING_CONFIG,
  COLOR_CONFIG,
  FONT_CONFIG
} from './constants/config';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`kpi-tabpanel-${index}`}
      aria-labelledby={`kpi-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `kpi-tab-${index}`,
    'aria-controls': `kpi-tabpanel-${index}`,
  };
}

const categoryConfigs = CATEGORY_CONFIGS;

function App() {
  const [tabValue, setTabValue] = useState(0);
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleKPIClick = async (kpi: KPIIndicator) => {
    setLoading(true);
    
    // 创建新的 AbortController
    const controller = new AbortController();
    setAbortController(controller);
    
    try {
      // 特殊处理 AI 分析
      if (kpi.category === 'ai-analytics') {
        // 处理 AI 智能对话功能
        if (kpi.id === 'ai-chat-assistant') {
          setChatDialogOpen(true);
          return;
        }
        
        setNotification({
          open: true,
          message: NOTIFICATION_MESSAGES.AI_ANALYSIS_START,
          severity: 'info'
        });
        
        // 获取所有KPI数据用于AI分析
        const allKPIData: AIKPIData[] = kpiData.map(kpi => ({
          id: kpi.id,
          title: kpi.title,
          value: String(kpi.data[0]?.value || '0'),
          trend: kpi.data[0]?.trend || 'stable'
        }));
        
        console.log(LOG_MESSAGES.PREPARING_ANALYSIS.replace('{count}', allKPIData.length.toString()));
        
        // 先显示加载状态
        setModalData({
          title: kpi.title,
          description: kpi.description,
          charts: undefined,
          isLoading: true,
        });
        setModalOpen(true);
        
        // 调用真实的AI服务，传递 AbortController
        const aiResult = await performAIAnalysis(
          allKPIData,
          'AI运营分析',
          kpi.subcategory,
          controller
        );
        
        setModalData({
          title: kpi.title,
          description:
            kpi.description +
            '\n\n🤖 DeepSeek AI 分析结果：\n\n' +
            aiResult.analysis +
            '\n\n分析置信度：' +
            (aiResult.confidence * 100).toFixed(1) +
            '%',
          charts: undefined, // AI 分析不使用图表，只显示 bullet points
          isLoading: false,
        });

        setNotification({
          open: true,
          message: NOTIFICATION_MESSAGES.AI_ANALYSIS_SUCCESS,
          severity: 'success'
        });
      } else {
        const charts = chartConfigs[kpi.id];
        setModalData({
          title: kpi.title,
          description: kpi.description,
          charts: charts || undefined,
        });
      }
      
      setModalOpen(true);
    } catch (error: any) {
      console.error(ERROR_MESSAGES.AI_ANALYSIS_FAILED, error);
      
      // 检查是否是用户取消操作
      if (error.name === 'AbortError' || error.message?.includes('canceled')) {
        console.log(ERROR_MESSAGES.USER_CANCELLED);
        return; // 直接返回，不显示任何错误信息
      }
      
      setNotification({
        open: true,
        message: `${ERROR_MESSAGES.AI_ANALYSIS_FAILED} ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setAbortController(null); // 清理 AbortController
    }
  };

  const handleModalClose = () => {
    // 如果有正在进行的API调用，取消它
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    
    setModalOpen(false);
    setModalData(null);
  };

  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const getKPIsBySubcategory = (category: string, subcategory: string) => {
    return kpiData.filter(kpi => 
      kpi.category === category && kpi.subcategory === subcategory
    );
  };

  // 根据不同的tab值返回不同的Grid配置
  const getGridConfig = (tabValue: number) => {
    switch (tabValue) {
      case 0: // AI分析 - 一列居中
        return GRID_CONFIGS.AI_ANALYTICS;
      case 1: // 获客 - 一列居中
        return GRID_CONFIGS.ACQUISITION;
      case 2: // 激活 - 两列
        return GRID_CONFIGS.ACTIVATION;
      case 3: // 留存 - 三列
        return GRID_CONFIGS.RETENTION;
      case 4: // 收入 - 两列
        return GRID_CONFIGS.REVENUE;
      default:
        return GRID_CONFIGS.DEFAULT;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          background: BACKGROUND_GRADIENTS.HEADER,
          color: STYLE_CONSTANTS.COLORS.WHITE,
          py: STYLE_CONSTANTS.SPACING.HEADER_PADDING,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: `1px solid ${STYLE_CONSTANTS.COLORS.BORDER}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: BACKGROUND_GRADIENTS.BEFORE_PSEUDO,
            opacity: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: BACKGROUND_GRADIENTS.AFTER_PSEUDO,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              // position: 'relative',
              zIndex: 2,
              textAlign: 'center',
            }}
          >
                         {/* MC Markets Logo - Top Left */}
             <Fade in timeout={ANIMATION_CONFIG.FADE.TIMEOUT}>
               <Box
                 sx={{
                   position: 'absolute',
                   top: 20,
                   left: 20,
                   zIndex: 10,
                 }}
               >
                                     <Box
                     component="img"
                     src={HEADER_CONSTANTS.LOGO_SRC}
                     alt={HEADER_CONSTANTS.LOGO_ALT}
                     sx={{
                       objectFit: 'contain',
                       filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))',
                     }}
                   />
               </Box>
             </Fade>
            
                         <Fade in timeout={ANIMATION_CONFIG.FADE.TIMEOUT}>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 300,
                  mb: 2,
                  color: STYLE_CONSTANTS.COLORS.WHITE,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  fontFamily: STYLE_CONSTANTS.FONTS.MONO,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60px',
                    height: '2px',
                    background: STYLE_CONSTANTS.COLORS.PRIMARY,
                  },
                }}
              >
                                 {HEADER_CONSTANTS.TITLE}
              </Typography>
            </Fade>
            
                         <Zoom in timeout={ANIMATION_CONFIG.ZOOM.TIMEOUT}>
              <Typography
                variant="h5"
                component="p"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  fontWeight: 300,
                  color: STYLE_CONSTANTS.COLORS.LIGHT_GRAY,
                  position: 'relative',
                  zIndex: 1,
                  letterSpacing: '0.05em',
                  fontFamily: STYLE_CONSTANTS.FONTS.SANS,
                  textTransform: 'none',
                }}
              >
                                 {HEADER_CONSTANTS.SUBTITLE}
              </Typography>
            </Zoom>
            
            
          </Box>
        </Container>
        
        {/* Animated particles */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          {[...Array(PARTICLE_CONFIG.COUNT)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: `${PARTICLE_CONFIG.MIN_SIZE + Math.random() * (PARTICLE_CONFIG.MAX_SIZE - PARTICLE_CONFIG.MIN_SIZE)}px`,
                height: `${PARTICLE_CONFIG.MIN_SIZE + Math.random() * (PARTICLE_CONFIG.MAX_SIZE - PARTICLE_CONFIG.MIN_SIZE)}px`,
                background: `${Math.random() > 0.5 ? PARTICLE_CONFIG.COLORS.BLUE : PARTICLE_CONFIG.COLORS.GREEN}${0.4 + Math.random() * 0.4})`,
                borderRadius: '50%',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `particle ${PARTICLE_CONFIG.ANIMATION.MIN_DURATION + Math.random() * (PARTICLE_CONFIG.ANIMATION.MAX_DURATION - PARTICLE_CONFIG.ANIMATION.MIN_DURATION)}s ease-in-out infinite`,
                animationDelay: `${Math.random() * PARTICLE_CONFIG.ANIMATION.MAX_DELAY}s`,
                '@keyframes particle': PARTICLE_KEYFRAMES,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Navigation Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="xl">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'fullWidth'}
            scrollButtons={isMobile ? 'auto' : false}
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                fontSize: '1rem',
                fontWeight: 500,
              },
            }}
          >
            {categoryConfigs.map((config) => (
              <Tab
                key={config.value}
                label={config.label}
                {...a11yProps(config.value)}
                sx={{
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                }}
              />
            ))}
          </Tabs>
        </Container>
      </Paper>

      {/* Content */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {categoryConfigs.map((config) => {
          const gridConfig = getGridConfig(config.value);
          
          return (
            <TabPanel key={config.value} value={tabValue} index={config.value}>
              <Grid container {...gridConfig.containerProps}>
                {config.subcategories.map((subcategory, index) => {
                  const kpis = getKPIsBySubcategory(config.category, subcategory.title);
                  if (kpis.length === 0) return null;

                  return (
                    <Grid item {...gridConfig.itemProps} key={index}>
                      <KPICategory
                        title={subcategory.title}
                        subtitle={subcategory.subtitle}
                        icon={subcategory.icon}
                        color={subcategory.color}
                        kpis={kpis}
                        onKPIClick={handleKPIClick}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </TabPanel>
          );
        })}
      </Container>

      {/* Chart Modal */}
      <ChartModal
        open={modalOpen}
        onClose={handleModalClose}
        data={modalData}
      />

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

             {/* Notification Snackbar */}
               <Snackbar
          open={notification.open}
          autoHideDuration={NOTIFICATION_CONFIG.AUTO_HIDE_DURATION}
          onClose={handleNotificationClose}
          anchorOrigin={NOTIFICATION_CONFIG.ANCHOR_ORIGIN}
        >
         <Alert 
           onClose={handleNotificationClose} 
           severity={notification.severity}
           sx={{ width: '100%' }}
         >
           {notification.message}
         </Alert>
       </Snackbar>

       {/* AI Chat Dialog */}
       <AIChatDialog
         open={chatDialogOpen}
         onClose={() => setChatDialogOpen(false)}
       />
     </Box>
   );
 }

export default App; 